import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, KeyRound, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { LOGOS } from "../../lib/ablemp-data";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Link inválido — token ausente.");
      setChecking(false);
      return;
    }
    api
      .get(`/auth/reset-password/${encodeURIComponent(token)}`)
      .then((r) => setPreview(r.data))
      .catch((err) => {
        setError(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "Link inválido ou expirado."
        );
      })
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("A senha deve ter no mínimo 8 caracteres.");
      return;
    }
    if (pw !== pw2) {
      toast.error("As senhas não coincidem.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Senha redefinida com sucesso!");
      setTimeout(() => navigate("/login", { replace: true }), 1600);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Erro ao redefinir a senha."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F14] text-white grid md:grid-cols-2">
      <div className="hidden md:flex items-center justify-center p-10">
        <div className="max-w-sm">
          <img src={LOGOS.white} alt="ABLEMP" className="h-14 mb-8" />
          <div className="text-[11px] uppercase tracking-[0.32em] text-white/60">
            Portal do Associado
          </div>
          <h1 className="mt-3 font-serif text-4xl leading-tight">
            Criar uma <em className="text-[#FEDF00] not-italic font-medium">nova senha</em>.
          </h1>
          <p className="mt-5 text-sm text-white/70 leading-relaxed">
            Escolha uma senha forte com ao menos 8 caracteres. Ela será usada
            para acessar o portal a partir de agora.
          </p>
        </div>
      </div>
      <div className="bg-white text-slate-900 flex items-center justify-center p-8">
        <div className="w-full max-w-md" data-testid="reset-password-page">
          <div className="mb-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
              Redefinição
            </div>
            <h2 className="mt-1 font-serif text-3xl">Nova senha</h2>
          </div>

          {checking && (
            <div className="text-sm text-slate-500">
              Verificando link...
            </div>
          )}

          {!checking && error && (
            <div
              data-testid="reset-error"
              className="border border-rose-200 bg-rose-50 text-rose-800 rounded-sm p-4 text-sm"
            >
              <div className="font-semibold mb-1">Link inválido</div>
              <div>{error}</div>
              <div className="mt-3">
                <Link
                  to="/login"
                  className="text-[11px] uppercase tracking-[0.22em] underline underline-offset-4"
                >
                  Voltar para o login
                </Link>
              </div>
            </div>
          )}

          {!checking && preview && !done && (
            <form onSubmit={submit} data-testid="reset-form">
              <div className="text-sm text-slate-600 mb-4">
                Você está redefinindo a senha para{" "}
                <strong>{preview.name}</strong>
                {preview.email_masked && (
                  <span className="text-slate-500"> · {preview.email_masked}</span>
                )}
                .
              </div>

              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Nova senha
              </label>
              <input
                data-testid="reset-pw"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                minLength={8}
                required
                autoFocus
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
                placeholder="Mínimo 8 caracteres"
              />

              <label className="mt-4 block text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Confirmar nova senha
              </label>
              <input
                data-testid="reset-pw2"
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                minLength={8}
                required
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
                placeholder="Repita a nova senha"
              />

              <button
                data-testid="reset-submit"
                type="submit"
                disabled={busy}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#0A0F14] text-white py-3 text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-[#002776] transition-colors disabled:opacity-60"
              >
                <KeyRound size={14} />
                {busy ? "Salvando..." : "Salvar nova senha"}
              </button>

              <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-500">
                <ShieldCheck size={14} />
                Este link é de uso único e expira em 15 minutos.
              </div>
            </form>
          )}

          {done && (
            <div
              data-testid="reset-success"
              className="border border-emerald-200 bg-emerald-50 text-emerald-800 rounded-sm p-4 text-sm"
            >
              <div className="flex items-center gap-2 font-semibold">
                <CheckCircle2 size={16} />
                Senha redefinida com sucesso.
              </div>
              <div className="mt-2 text-slate-600">
                Redirecionando para o login...
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
